import {
  Planner_Notification_Type,
  type PlannerNotificationSettingsFragment,
  type PlannerTaskFragment,
} from "@/graphql/generated-types";
import type { IActorIds, IEditEntry } from "@/planner-interfaces";
import type { ICategoryItem } from "@/components/form/elements/lists/CategoryList.vue";
import SaveActions from "@/services/enums/saveactions";
import toRRuleState from "@/helpers/rrules/toRRuleState";
import getDefaultRRuleState from "@/helpers/rrules/getDefaultRRuleState";
import { toUnix } from "@/stories/utils/dates";
import type { RRuleState } from "@/types";
import { DEFAULT_RRULE_DAYS_STATE } from "@/consts";
import { addDays, getISODay } from "date-fns";
import type { IAssigneeNotificationSettings } from "@/components/form/elements/ActorNotificationSettingsInput.vue";

type ActorTypes = "resident" | "employee" | "department";

export const getDefaultSelectedDaysForWeek = () => {
  const days = { ...DEFAULT_RRULE_DAYS_STATE };
  // Subtract 1 to get 0 (Mon) - 6 (Sun)
  const currentDay = getISODay(new Date()) - 1;
  // Selecting today
  days[currentDay] = true;

  return days;
};

export const getDefaultPlannerTaskRRuleState = (): RRuleState => {
  return {
    ...getDefaultRRuleState(),
    days: getDefaultSelectedDaysForWeek(),
    startDate: toUnix(new Date()),
    endingType: "no_end_date",
    endingDate: toUnix(addDays(new Date(), 7)),
    cadence: 7,
    daySelection: 0,
    dayOrder: 1,
  };
};

const getDefaultNotificationSettings = (): IAssigneeNotificationSettings => {
  return {
    state: "none",
    notifications: {
      planner_first: null,
      planner_second: null,
      planner_third: null,
      planner_fourth: null,
    },
    triggers: {
      change: false,
      complete: false,
      create: false,
      overdue: false,
    },
    type: Planner_Notification_Type.Resident,
  };
};

function toNotificationSetting(settings?: PlannerNotificationSettingsFragment) {
  if (!settings) {
    return getDefaultNotificationSettings();
  }

  const data: IAssigneeNotificationSettings = {
    triggers: {
      create: settings.triggerWhenNewAssignment || false,
      change: settings.triggerWhenAssignmentChange || false,
      complete: settings.triggerWhenAssignmentComplete || false,
      overdue: settings.triggerWhenAssignmentOverdue || false,
    },
    notifications: {
      planner_first: settings.reminderFirst ?? null,
      planner_second: settings.reminderSecond ?? null,
      planner_third: settings.reminderThird ?? null,
      planner_fourth: settings.reminderFourth ?? null,
    },
    state: settings.state,
    type: settings.notificationType,
  };

  return data;
}

export function normalizeNewTaskEntry(
  entry: PlannerTaskFragment,
  categoryList: ICategoryItem[],
): Partial<IEditEntry> {
  const actorIds: IActorIds = {
    resident: [],
    employee: [],
    department: [],
  };

  entry.assignees?.forEach((assignee) => {
    const type = assignee.type.toLowerCase() as ActorTypes;
    if (type === "resident" || type === "employee" || type === "department") {
      actorIds[type].push(assignee.id);
    }
  });

  const category = categoryList.find(
    (x) => x.id == parseInt(entry.category?.id || ""),
  );

  // Normalize image (picture or pictogram)
  const image = entry.picture?.source || entry.pictogram?.source;
  const images = image
    ? [
        {
          image: image,
          thumbnail: image,
          saveAction: SaveActions.NONE,
          source: entry.pictogram?.source ? "pictogram" : "upload",
          sourceId: null,
        },
      ]
    : [];

  const start = new Date(entry.startDate);
  const end = new Date(entry.endDate);

  const isRepeat = entry.__typename === "RepeatingPlannerTask";
  let rruleState: RRuleState;
  let repeatId: number | null = null;

  if (isRepeat && entry.repeat?.rset) {
    rruleState = toRRuleState(entry.repeat.rset);
    repeatId = parseInt(entry.repeat.id);
  } else {
    rruleState = getDefaultPlannerTaskRRuleState();
  }

  // Ensure rruleState.startDate is synchronized with the task's actual start date
  rruleState.startDate = toUnix(start);

  const visibleForResident = entry.visibleForResident === true;

  const subtasks = (entry.subtasks || []).map((subtask) => {
    const images: any = [];

    const toImage = (source: string) => ({
      image: source,
      thumbnail: source,
      saveAction: null,
      source: "upload",
      sourceId: null,
    });

    if (subtask.picture) {
      images.push(toImage(subtask.picture.source));
    }

    if (subtask.pictogram) {
      images.push(toImage(subtask.pictogram.source));
    }

    return {
      completed: subtask.completed,
      pictogramId: subtask.pictogram?.id,
      pictogramPath: subtask.pictogram?.source,
      resourceId: subtask.picture?.id,
      resourcePath: subtask.picture?.source,
      sortorder: subtask.sortOrder?.toString() || "",
      images,
      id: subtask.id,
      subtaskId: subtask.id,
      title: subtask.title,
      image: {},
    };
  });

  return {
    id: entry.id,
    repeatId,
    title: entry.title,
    description: entry.description || "",
    approvalState: entry.approvalState || "unneeded",
    visibleForResident,
    category,
    categoryId: entry.category?.id,
    isRepeat,
    subtasks,
    images,
    start,
    end,
    actorIds,
    notificationSettings: {
      resident: toNotificationSetting(
        entry.notificationSettings?.find(
          (item) =>
            item.notificationType === Planner_Notification_Type.Resident,
        ),
      ),
      employee: toNotificationSetting(
        entry.notificationSettings?.find(
          (item) =>
            item.notificationType === Planner_Notification_Type.Employee,
        ),
      ),
    },
    rruleState,
  };
}
